import {
  Checkbox,
  CheckboxGroup, FormControl,
  FormLabel,
  Grid, HStack,
  Radio,
  RadioGroup, Switch,
  VStack
} from '@chakra-ui/react'
import type { Addon, InsuranceParamsDto, InsuranceParamsLeasingDto } from 'BrokerTsLib/src/dtos'
import { ToAddon } from 'BrokerTsLib/src/dtos'
import { Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { nameof } from 'ts-simple-nameof'

import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useEffect } from 'react'
import { useGetRiskVariants, useInsurers } from '../hooks'
import Car from '../images/car.svg?react'
import CarCrash from '../images/carCrash.svg?react'
import CarPercent from '../images/carPercent.svg?react'
import { CustomSlider } from './CustomSlider'
import { InsuranceCompanies } from './InsuranceCompanies'
import { LabelWithIcon } from './ui/LabelWithIcon'
import { SliderLabel } from './ui/SliderLabel'

export const LeasingBasicParametersForm = () => {
  const { translate } = useTranslationsContext()
  const { data: dataPov } = useGetRiskVariants(Risk.POV)
  const { data: dataHav } = useGetRiskVariants(Risk.HAV)
  const { data: dataSpol } = useGetRiskVariants(Risk.SPOL)
  const { data: dataAsistence } = useGetRiskVariants(Risk.ASISTENCE)
  const { data: dataSkla } = useGetRiskVariants(Risk.SKLA)
  const { data: dataNahr } = useGetRiskVariants(Risk.NAHR)
  const { data: dataDirectLiquidation } = useGetRiskVariants(Risk.DirectLiquidation)
  const { data: allInsurersData, isLoading: isLoadingInsurers, isFetching: isFetchingInsurers } = useInsurers(false, false)
  const allInsurers = allInsurersData?.data ?? []

  useEffect(() => {
    if (allInsurers.length > 0 && getValues('baseParams.insurers')?.length == 0)
      setValue(
        'baseParams.insurers',
        allInsurers.map(({ id }) => id),
      )
  }, [allInsurers.length])

  const { watch, setValue, getValues } = useFormContext<InsuranceParamsLeasingDto>()

  const addons = watch('addons') ?? []
  const fixedPrice = watch('leasingData.fixedPrice')

  const defaultAssistance = (dataAsistence?.data.length ?? 0) > 0 ? dataAsistence?.data[0] : undefined
  const selectedAssistance = addons.find(x => x.riskId == Risk.ASISTENCE) ?? { riskId: Risk.ASISTENCE, riskVariantId: defaultAssistance?.id }
  const assistance = dataAsistence?.data.find(x => x.id == selectedAssistance?.riskVariantId)
  const assistanceSliderDS = dataAsistence?.data.map((item, index) => { return { i: index, ...item } }) ?? []
  const assistanceMin = assistanceSliderDS[0]
  const assistanceMax = assistanceSliderDS[assistanceSliderDS.length - 1]

  const defaultGlass = (dataSkla?.data.length ?? 0) > 0 ? dataSkla?.data[0] : undefined
  const selectedGlass = addons.find(x => x.riskId == Risk.SKLA) ?? { riskId: Risk.SKLA, riskVariantId: defaultGlass?.id }
  const glass = dataSkla?.data.find(x => x.id == selectedGlass?.riskVariantId)
  const glassSliderDS = dataSkla?.data.map((item, index) => { return { i: index, ...item } }).sort((x, y) => parseInt(x.value!) - parseInt(y.value!)) ?? []
  const glassMin = glassSliderDS[0]
  const glassMax = glassSliderDS[glassSliderDS.length - 1]

  const defaultNahr = (dataNahr?.data.length ?? 0) > 0 ? dataNahr?.data[0] : undefined
  const selectedNahr = addons.find(x => x.riskId == Risk.NAHR) ?? { riskId: Risk.NAHR, riskVariantId: defaultNahr?.id }
  const nahr = dataNahr?.data.find(x => x.id == selectedNahr?.riskVariantId)
  const nahrSliderDS = dataNahr?.data.map((item, index) => { return { i: index, ...item } }).sort((x, y) => parseInt(x.value!) - parseInt(y.value!)) ?? []
  const nahrMin = nahrSliderDS[0]
  const nahrMax = nahrSliderDS[nahrSliderDS.length - 1]

  const defaultDirectLiquidation = (dataDirectLiquidation?.data.length ?? 0) > 0 ? dataDirectLiquidation?.data[0] : undefined
  const selectedDirectLiquidation = addons.find(x => x.riskId == Risk.DirectLiquidation) ?? { riskId: Risk.DirectLiquidation, riskVariantId: defaultDirectLiquidation?.id }
  const directLiquidation = dataDirectLiquidation?.data.find(x => x.id == selectedDirectLiquidation?.riskVariantId)

  const hav = watch('baseParams.hav')

  const setAddon = (addon: Addon) => {
    let addons = getValues('addons') ?? []

    addons = addons.filter(x => x.riskId !== addon.riskId)
    addons.push(addon)

    setValue('addons', addons)
  }

  const showNahr = nahrSliderDS && nahrSliderDS.length > 0 && nahrMin && nahrMax

  return (
    <Grid sx={{ alignItems: 'start', gap: 14 }}>
      <Grid
        sx={{
          gridTemplateColumns: { md: 'repeat(3, 1fr)' },
          gap: 8,
          alignItems: 'start',
        }}
      >
        {/* Povinné ručení */}
        <FormControl sx={{ gap: 8 }}>
          <LabelWithIcon label={translate('step2.basicParameters.povLabel', 'Povinné ručení (mil. Kč)')} icon={<Car />} />

          <VStack sx={{ ml: 2 }} align="start" gap={3}>
            <CheckboxGroup
              value={watch('baseParams.pov')?.map(x => x.toString())}
              onChange={(val) => {
                setValue('baseParams.pov', val.map(x => parseInt(x.toString())))
              }}>
              {dataPov?.data.map(x => <Checkbox key={x.id} value={x.id.toString()}>{x.name}</Checkbox>)}
            </CheckboxGroup>
          </VStack>
        </FormControl>

        {/* Havarijní pojištění */}
        <FormControl sx={{ gap: 8 }}>
          <LabelWithIcon label={translate('step2.basicParameters.havLabel', 'Havarijní pojištění')} icon={<CarCrash />} />

          <VStack sx={{ ml: 2 }} align="start" gap={3}>
            <CheckboxGroup
              value={watch('baseParams.hav')?.map(x => x.toString())}
              onChange={(val) => {
                setValue('baseParams.hav', val.map(x => parseInt(x.toString())))
              }}>
              {dataHav?.data
                .filter(x => x.id != RiskVariant.Package)
                .map(x => <Checkbox key={x.id} value={x.id.toString()}>{x.name}</Checkbox>)}
            </CheckboxGroup>
          </VStack>
        </FormControl>

        {/* Spoluúčast */}
        <FormControl sx={{ gap: 8 }}>
          <LabelWithIcon label={translate('step2.basicParameters.complicityLabel', 'Spoluúčast')} icon={<CarPercent />} />

          <VStack sx={{ ml: 2 }} align="start" gap={3}>
            <CheckboxGroup
              value={watch('baseParams.spol')?.map(x => x.toString())}
              isDisabled={(hav?.filter(x => x != RiskVariant.WithoutHAV).length ?? 0) == 0}
              onChange={(val) => {
                setValue('baseParams.spol', val.map(x => parseInt(x.toString())))
              }}>
              {dataSpol?.data.map(x => <Checkbox key={x.id} value={x.id.toString()}>{x.name.replace(translate('complicity', 'Spoluúčast'), '')}</Checkbox>)}
            </CheckboxGroup>
          </VStack>
        </FormControl>
      </Grid>

      <Grid
        sx={{
          gridTemplateColumns: { lg: 'repeat(2, 1fr)' },
          gap: 16,
          alignItems: 'start',
          alignContent: 'start',
        }}
      >
        {/* Asistence */}
        {assistanceSliderDS && assistanceSliderDS.length > 0 && assistanceMin && assistanceMax && assistanceMin != assistanceMax &&
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel
              label={translate('step2.basicParameters.assistanceLabel', 'Asistence')}
              value={assistance?.name ?? translate('noAssistance', 'Bez asistence')} />

            <CustomSlider
              value={assistanceSliderDS.find(x => x.id == selectedAssistance?.riskVariantId)?.i ?? 0}
              labels={[
                { label: assistanceMin?.name ?? '', value: assistanceMin?.i },
                { label: assistanceMax?.name ?? '', value: assistanceMax?.i },
              ]}
              min={0}
              max={assistanceSliderDS.length - 1}
              step={1}
              onChange={(val) => {
                setAddon(ToAddon(assistanceSliderDS.find(x => x.i == val)?.id.toString() ?? '', Risk.ASISTENCE))
              }} />
          </FormControl>
        }

        {/* Skla */}
        {glassSliderDS && glassSliderDS.length > 0 && glassMin && glassMax && glassMin != glassMax &&
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel label={translate('step2.basicParameters.glassLabel', 'Skla')} value={glass?.value && priceFormat(parseInt(glass.value!), undefined, glass.name)} />

            <CustomSlider
              value={glassSliderDS.find(x => x.id == selectedGlass?.riskVariantId)?.i ?? 0}
              labels={[
                { label: priceFormat(parseInt(glassMin.value!), undefined, glassMin.name), value: parseInt(glassMin.value!) },
                { label: priceFormat(parseInt(glassMax.value!)), value: parseInt(glassMax.value!) },
              ]}
              min={0}
              max={glassSliderDS.length - 1}
              step={1}
              onChange={(val) => {
                setAddon(ToAddon(glassSliderDS.find(x => x.i == val)?.id.toString() ?? '', Risk.SKLA))
              }} />
          </FormControl>
        }

        {directLiquidation &&
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel label={translate('step2.basicParameters.directLiquidationLabel', 'Přímá likvidace')} value={directLiquidation?.name} />

            <FormControl display="flex" alignItems="center">
              <Switch
                id="liquidation"
                isChecked={directLiquidation?.id == RiskVariant.DirectLiquidationYes}
                onChange={e => {
                  setAddon({ riskId: Risk.DirectLiquidation, riskVariantId: e.target.checked ? RiskVariant.DirectLiquidationYes : RiskVariant.DirectLiquidationNo })
                }}
                colorScheme="green"
                size="lg"
                dir="rtl"
              />
            </FormControl>
          </FormControl>
        }

        {/* Náhradní vozidlo */}
        {showNahr &&
          <FormControl sx={{ gap: 4 }}>
            <SliderLabel label={translate('step2.basicParameters.loanCarLabel', 'Náhradní vozidlo')} value={nahr?.name} />

            <CustomSlider
              value={nahrSliderDS.find(x => x.id == selectedNahr?.riskVariantId)?.i ?? 0}
              labels={[
                { label: nahrMin.name, value: parseInt(nahrMin.value!) },
                { label: nahrMax.name, value: parseInt(nahrMax.value!) },
              ]}
              min={0}
              max={nahrSliderDS.length - 1}
              step={1}
              onChange={(val) => {
                setAddon(ToAddon(nahrSliderDS.find(x => x.i == val)?.id.toString() ?? '', Risk.NAHR))
              }}
            />
          </FormControl>
        }

        {/* Pojištovny */}
        <InsuranceCompanies
          companies={allInsurers}
          isLoadingCompanies={(isFetchingInsurers || isLoadingInsurers)}
          propPath={nameof<InsuranceParamsDto>(x => x.baseParams.insurers)}
          tooltip={translate('step2.basicParameters.insurersTooltip', 'Vyberte pojišťovny, které chcete zahrnout do kalkulace pojištění.')}
        />

        {/* Typ sazby */}
        <FormControl sx={{ gap: 4 }}>
          <FormLabel>{translate('pricingType', 'Typ sazby')}</FormLabel>

          <RadioGroup onChange={(val) => setValue('leasingData.fixedPrice', val == '1')} value={fixedPrice != undefined ? (fixedPrice ? '1' : '0') : undefined}>
            <HStack align="start" gap={8} py={4}>
              <Radio value="0">{translate('float', 'Float')}</Radio>
              <Radio value="1">{translate('fix', 'Fix')}</Radio>
            </HStack>
          </RadioGroup>
        </FormControl>
      </Grid>
    </Grid >
  )
}
